<template>
    <div
        id="favorites-search"
        class="favorites-search no-outline"
        tabindex="0"
        data-no-focus-scroll
    >
        <div class="favorites-search__wrapper">
            <div class="favorites-search__input-wrapper">
                <input
                    id="query"
                    ref="searchInput"
                    :value="props.modelValue"
                    class="favorites-search__input"
                    type="text"
                    data-type="query"
                    :placeholder="enterSearchText" 
                    :class="{'favorites-search__input_reduced': isFrench}"
                    @focusin="onFocus"
                    @focusout="onFocusout"
                    @keydown.enter="onEnterKey"
                    @input="onUpdateModelValue"
                >
                <common-icon :icon-name="'IconSearch'" class="favorites-search__icon" />
            </div>
        </div>
        <div v-show="props.notFound" class="favorites-search__empty-result">
            <div class="favorites-search__empty-result_padding">
                {{ searchFor }} "{{ modelValue }}"
            </div>
            {{ searchNothingWasFound }} <br>
            {{ searchTryDifferentKeyword }}
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, nextTick } from "vue"
    import { Strings } from '@/locales/types/enums';
    import { useI18n } from "vue-i18n";
    import type { ExposeInterface } from "./types";
    import { LangCodes } from "@/common/types/languages";

    const emit = defineEmits(['returnFocus', 'update:model-value', 'back-to-navigation']);
    const props = defineProps({
        modelValue: {
            type: String,
            required: true,
        },
        notFound: {
            type: Boolean,
            required: true,
        },
    });

    const { $navigator } = useNuxtApp();
    const { t, locale } = useI18n();

    const enterSearchText: string = t(Strings.SETTINGS_FAVORITES_SEARCH_ENTER_TEXT).toString();
    const searchFor: string = t(Strings.SETTINGS_FAVORITES_SEARCH_SEARCH_FOR).toString();
    const searchNothingWasFound: string = t(Strings.SETTINGS_FAVORITES_SEARCH_NOTHING_WAS_FOUND).toString();
    const searchTryDifferentKeyword: string = t(Strings.SETTINGS_FAVORITES_SEARCH_TRY_DIFFERENT_KEYWORD).toString();

    const isFrench: boolean = locale.value === LangCodes.FR;
    const isRegKeyListeners: Ref<boolean> = ref(false);

    const searchInput: Ref<HTMLInputElement| null> = ref(null);

    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    }
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", goToBack);
            $navigator.on("keypress:down", returnFocus);
            $navigator.on("keypress:left", goToBack);
            isRegKeyListeners.value = true;
        }
    }
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", goToBack);
            $navigator.off("keypress:down", returnFocus);
            $navigator.off("keypress:left", goToBack);
            isRegKeyListeners.value = false;
        }
    }
    const returnFocus = (): void => {
        emit('returnFocus');
        unregisterKeyListeners();
    }
    const goToBack = (): void => {
        emit("back-to-navigation");
        unregisterKeyListeners();
    }
    const onEnterKey = (e: Event): void => {
        e.preventDefault();
        searchInput.value?.blur();
       void nextTick(() => {
            returnFocus();
        })
    }
    const focusToInput = (): void => {
        searchInput.value?.focus();
    };

    const onUpdateModelValue = (value: Event): void => {
        const target = value.target as HTMLInputElement;
        emit('update:model-value', target.value);
    }

    defineExpose<ExposeInterface>({
        focusToInput,
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
